/* @jsx h */
import { defineComponent, ref, onMounted, h } from "vue";
import {
  fetchCustomerAddresses,
  createCustomerAddress,
  deleteCustomerAddress,
} from "@app/api/client/index";
import {
  CustomerAddressEditable,
  CustomerProfileAddress,
  ShopSettingDeliveryCity,
} from "@lemmonhq/types";
import { RightSheet, useLocalization } from "@lemmonhq/uikit";
import { cn } from "@lemmonhq/uikit";
import { CardsSkeleton } from "./AccountSkeleton";

export type CustomerAddressManagerFormProps = {
  onClose: () => void;
  onSuccess: () => void;
  deliveryCities?: ShopSettingDeliveryCity[];
};
export const CustomerAddressManagerForm =
  defineComponent<CustomerAddressManagerFormProps>({
    props: ["onClose", "onSuccess", "deliveryCities"],
    setup(props) {
      const { t } = useLocalization();
      const newAddress = ref<CustomerAddressEditable>({
        name: "",
        company_name: "",
        street: "",
        postal_code: "",
        city:
          props.deliveryCities && props.deliveryCities.length > 0
            ? props.deliveryCities[0].name
            : "",
        phone: "",
        email: "",
        notes: "",
      });
      const isSubmitting = ref(false);

      const validateForm = () => {
        const address = newAddress.value;
        return (
          address.name.trim() !== "" &&
          address.street.trim() !== "" &&
          address.postal_code.trim() !== "" &&
          address.city.trim() !== "" &&
          address.phone.trim() !== "" &&
          address.email.trim() !== ""
        );
      };

      const handleAddAddress = async () => {
        if (!validateForm()) {
          alert(t("customer.account.required_fields"));
          return;
        }

        try {
          isSubmitting.value = true;
          await createCustomerAddress(newAddress.value);
          resetForm();
          props.onSuccess();
        } catch (err) {
          alert(t("customer.account.error_saving_address"));
          console.error("Error creating address:", err);
        } finally {
          isSubmitting.value = false;
        }
      };

      const resetForm = () => {
        newAddress.value = {
          name: "",
          company_name: "",
          street: "",
          postal_code: "",
          city: "",
          phone: "",
          email: "",
          notes: "",
        };
      };
      return () => {
        return (
          <RightSheet
            title={t("customer.account.add_address")}
            onClose={props.onClose}
            isOpen={true}
          >
            <div class=" rounded-lg p-6">
              <div class="flex justify-between items-center mb-4">
                <h4 class="font-semibold">
                  {t("customer.account.new_address")}
                </h4>
                <button
                  class="btn btn-ghost btn-sm"
                  onClick={() => {
                    props.onClose();
                    resetForm();
                  }}
                >
                  <span class="material-symbols-outlined">close</span>
                </button>
              </div>

              <div class="space-y-4">
                <input
                  v-model={newAddress.value.name}
                  class="input input-bordered w-full"
                  placeholder={t("customer.account.name")}
                  required
                />

                <input
                  v-model={newAddress.value.company_name}
                  class="input input-bordered w-full"
                  placeholder={t("customer.account.company_name_optional")}
                />

                <input
                  v-model={newAddress.value.street}
                  class="input input-bordered w-full"
                  placeholder={t("customer.account.street_address")}
                  required
                />

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    v-model={newAddress.value.postal_code}
                    class="input input-bordered"
                    placeholder={t("customer.account.postal_code")}
                    required
                  />
                  {props.deliveryCities ? (
                    <select
                      v-model={newAddress.value.city}
                      class="input input-bordered"
                      placeholder={t("customer.account.city")}
                      required
                    >
                      {props.deliveryCities.map((city, i) => {
                        return (
                          <option key={i} value={city.name}>
                            {city.name}
                          </option>
                        );
                      })}
                    </select>
                  ) : (
                    <input
                      v-model={newAddress.value.city}
                      class="input input-bordered"
                      placeholder={t("customer.account.city")}
                      required
                    />
                  )}
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    v-model={newAddress.value.phone}
                    class="input input-bordered"
                    placeholder={t("customer.account.phone_number")}
                    required="true"
                  />
                  <input
                    v-model={newAddress.value.email}
                    class="input input-bordered"
                    placeholder={t("customer.account.email_address")}
                    type="email"
                    required="true"
                  />
                </div>

                <textarea
                  v-model={newAddress.value.notes}
                  class="textarea textarea-bordered w-full"
                  placeholder={t("customer.account.additional_notes_optional")}
                  rows="2"
                />

                <div class="flex gap-2 justify-end">
                  <button
                    class="btn btn-ghost"
                    onClick={() => {
                      resetForm();
                      props.onClose();
                    }}
                  >
                    {t("customer.account.cancel")}
                  </button>
                  <button
                    class="btn btn-primary"
                    onClick={handleAddAddress}
                    disabled={isSubmitting.value || !validateForm()}
                  >
                    {isSubmitting.value ? (
                      <span class="loading loading-spinner loading-sm"></span>
                    ) : (
                      t("customer.account.save_address")
                    )}
                  </button>
                </div>
              </div>
            </div>
          </RightSheet>
        );
      };
    },
  });

export const CustomerAddressCard = defineComponent<{
  address: CustomerProfileAddress;
  onDelete: (id: string) => void;
  showRadio?: boolean;
  selected?: boolean;
  onClick?: () => void;
  disabled?: boolean;
}>({
  props: [
    "address",
    "onDelete",
    "showRadio",
    "selected",
    "onClick",
    "disabled",
  ],
  setup(props) {
    const { t } = useLocalization();

    return () => (
      <div
        onClick={props.disabled ? undefined : props.onClick}
        class={cn(
          "rounded-2xl p-4 border border-base-300 bg-base-200/40",
          props.selected && "border-primary",
          props.onClick && !props.disabled && "cursor-pointer",
          props.disabled && "opacity-50 cursor-not-allowed",
        )}
      >
        <div class="flex justify-between items-start">
          {props.showRadio ? (
            <div class="flex justify-center items-center pr-2">
              <input
                type="radio"
                class="radio radio-primary"
                checked={props.selected}
                disabled={props.disabled}
              />
            </div>
          ) : null}
          <div class="flex-1 space-y-2">
            <div class="">
              <span class="text-base-content/50 uppercase text-xs">
                {t("customer.account.name")}
              </span>
              <div class="">{props.address.name}</div>
            </div>

            {props.address.company_name && (
              <div class="">
                <span class="text-base-content/50 uppercase text-xs font-medium">
                  {t("customer.account.company")}
                </span>
                <div class="">{props.address.company_name}</div>
              </div>
            )}

            <div class="">
              <span class="text-base-content/50 uppercase text-xs font-medium">
                {t("customer.account.street_address")}
              </span>
              <div class="">{props.address.street}</div>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <span class="text-base-content/50 uppercase text-xs font-medium">
                  {t("customer.account.postal_code")}
                </span>
                <div class="">{props.address.postal_code}</div>
              </div>
              <div>
                <span class="text-base-content/50 uppercase text-xs font-medium">
                  {t("customer.account.city")}
                </span>
                <div class="">{props.address.city}</div>
              </div>
            </div>

            <div class="grid grid-cols-1 gap-4 ">
              <div>
                <span class="text-base-content/50 uppercase text-xs font-medium">
                  {t("customer.account.email")}
                </span>
                <div class=" flex items-center gap-1 text-wrap break-all">
                  <span class="material-symbols-outlined text-xs hidden">
                    mail
                  </span>
                  {props.address.email}
                </div>
              </div>
              <div>
                <span class="text-base-content/50 uppercase text-xs font-medium break-all">
                  {t("customer.account.phone")}
                </span>
                <div class=" flex items-center gap-1 text-wrap">
                  <span class="material-symbols-outlined text-xs hidden">
                    phone
                  </span>
                  {props.address.phone}
                </div>
              </div>
            </div>

            {props.address.notes && (
              <div class="">
                <span class="text-base-content/50 uppercase text-xs font-medium">
                  {t("customer.account.notes")}
                </span>
                <div class=" italic">{props.address.notes}</div>
              </div>
            )}
          </div>
          <button
            class="btn btn-ghost btn-sm text-error hover:bg-error/10"
            onClick={(e) => {
              e.stopPropagation();
              props.onDelete(props.address.id);
            }}
          >
            <span class="material-symbols-outlined">delete</span>
          </button>
        </div>
      </div>
    );
  },
});

export const CustomerAddressManager = defineComponent({
  setup() {
    const { t } = useLocalization();
    const addresses = ref<CustomerProfileAddress[]>([]);
    const isLoading = ref(true);
    const showAddForm = ref(false);
    const error = ref<string | null>(null);

    const loadAddresses = async () => {
      try {
        isLoading.value = true;
        error.value = null;
        const data = await fetchCustomerAddresses();
        addresses.value = data;
      } catch (err) {
        error.value = t("customer.account.failed_to_load_addresses");
        console.error("Error loading addresses:", err);
      } finally {
        isLoading.value = false;
      }
    };

    const handleDeleteAddress = async (addressId: string) => {
      if (!confirm(t("customer.account.confirm_delete_address"))) return;

      try {
        await deleteCustomerAddress(addressId);
        await loadAddresses();
      } catch (err) {
        error.value = t("customer.account.failed_to_delete_address");
        console.error("Error deleting address:", err);
      }
    };

    onMounted(() => {
      loadAddresses();
    });

    return () => (
      <div class="space-y-6">
        {error.value && (
          <div class="alert alert-error">
            <span class="material-symbols-outlined">error</span>
            <span>{error.value}</span>
          </div>
        )}

        <div class="flex justify-between items-center">
          <h3 class="text-lg font-semibold"></h3>
          <button
            class="btn btn-primary btn-sm"
            onClick={() => (showAddForm.value = !showAddForm.value)}
          >
            <span class="material-symbols-outlined mr-1">add</span>
            {t("customer.account.add_address")}
          </button>
        </div>
        {showAddForm.value ? (
          <CustomerAddressManagerForm
            onClose={() => {
              showAddForm.value = false;
            }}
            onSuccess={() => {
              loadAddresses();
              showAddForm.value = false;
            }}
          />
        ) : null}
        {isLoading.value ? (
          <CardsSkeleton cards={2} />
        ) : addresses.value.length === 0 ? (
          <div class="rounded-2xl border border-base-300 bg-base-200/40 px-6 py-14 text-center">
            <span class="mx-auto grid size-14 place-items-center rounded-2xl bg-base-200 text-base-content/30">
              <span
                class="material-symbols-outlined text-3xl"
                aria-hidden="true"
              >
                location_on
              </span>
            </span>
            <h3 class="mt-4 text-lg font-bold text-base-content">
              {t("customer.account.no_addresses")}
            </h3>
            <p class="mx-auto mt-1 mb-6 max-w-sm text-sm text-base-content/60">
              {t("customer.account.no_addresses_copy")}
            </p>
            <button
              class="btn btn-primary btn-sm gap-2"
              onClick={() => (showAddForm.value = true)}
            >
              <span class="material-symbols-outlined text-lg">add</span>
              {t("customer.account.add_address")}
            </button>
          </div>
        ) : (
          <div class="space-y-4">
            {addresses.value.map((address) => (
              <CustomerAddressCard
                key={address.id}
                address={address}
                onDelete={handleDeleteAddress}
              />
            ))}
          </div>
        )}
      </div>
    );
  },
});
